import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { deleteUserMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFormik } from 'formik';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export const DeleteAccountDialog: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const { dismissActiveDialog } = useDialog();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmText: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (values.confirmText !== 'DELETE') {
        errors.confirmText = 'Please type DELETE to confirm';
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const t = toast.loading('Deleting account...');
      try {
        await execute(deleteUserMutation, { id: userId, password: _values.password });
        toast.success('Account deleted', { id: t });
      } catch (error) {
        toast.error('Failed to delete account', { id: t });
      }
      dismissActiveDialog();
      navigate('/auth/logout', { replace: true });
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-0.5'>
        <h3 className='font-semibold text-rose-600'>Delete Account</h3>
        <p className='text-sm leading-none text-slate-700'>
          This action cannot be undone. This will permanently delete your account and remove all your data from our
          servers.
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='password'>Current Password</Label>
          <Input
            id='password'
            name='password'
            type='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter your current password'
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmText'>Type DELETE to confirm</Label>
          <Input
            id='confirmText'
            name='confirmText'
            value={formik.values.confirmText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Type DELETE'
          />
          {formik.errors.confirmText && <p className='text-sm text-red-600'>{formik.errors.confirmText}</p>}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            Cancel
          </Button>
          <Button type='submit' variant='destructive' disabled={formik.isSubmitting}>
            Delete Account
          </Button>
        </div>
      </form>
    </div>
  );
};
